import { useMemo, useState } from 'react';
import type { WireIncident, WireService } from '@shared/contract';
import { duration } from '@/lib/format';
import { SeverityChip, StatePill } from '@/components/chips';

type IncidentFilter = 'open' | 'all';

interface Props {
  incidents: WireIncident[];
  services: WireService[];
  onAck: (id: number) => void;
  onResolve: (id: number) => void;
}

export function IncidentsView({ incidents, services, onAck, onResolve }: Props) {
  const [filter, setFilter] = useState<IncidentFilter>('open');
  const byId = useMemo(() => new Map(services.map((s) => [s.id, s])), [services]);

  const rows = filter === 'open' ? incidents.filter((i) => !i.resolvedAt) : incidents;

  return (
    <section className="view is-active" aria-label="Incidents">
      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Filter incidents">
          {(['open', 'all'] as IncidentFilter[]).map((value) => (
            <button
              key={value}
              className={`seg${filter === value ? ' is-active' : ''}`}
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >
              {value === 'open' ? 'Open' : 'All'}
            </button>
          ))}
        </div>
      </div>

      {!rows.length ? (
        <div className="empty">
          <strong>{filter === 'open' ? 'No open incidents' : 'No incidents recorded'}</strong>
          {filter === 'open' ? 'Everything is currently healthy.' : 'Nothing has failed yet.'}
        </div>
      ) : (
        <div className="panel">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Sev</th>
                  <th>Service</th>
                  <th>Status</th>
                  <th>Detail</th>
                  <th>Duration</th>
                  <th>State</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((incident) => {
                  const service = byId.get(incident.serviceId);
                  const state = incident.resolvedAt
                    ? 'resolved'
                    : incident.ackedAt
                      ? 'acked'
                      : 'open';
                  return (
                    <tr key={incident.id}>
                      <td>
                        <SeverityChip severity={incident.severity} />
                      </td>
                      <td className="nowrap">{service?.name ?? `#${incident.serviceId}`}</td>
                      <td className={`st-${incident.status}`}>{incident.status}</td>
                      <td>
                        {incident.summary}
                        {incident.rootIncidentId && (
                          <span className="muted">
                            {' '}
                            (rolled up under #{incident.rootIncidentId})
                          </span>
                        )}
                      </td>
                      <td className="muted nowrap">
                        {duration(incident.openedAt)}
                        {incident.resolvedAt ? ' total' : ''}
                      </td>
                      <td>
                        <StatePill state={state} />
                      </td>
                      <td className="nowrap">
                        {!incident.resolvedAt && (
                          <>
                            {!incident.ackedAt && (
                              <button className="btn" onClick={() => onAck(incident.id)}>
                                Ack
                              </button>
                            )}{' '}
                            <button className="btn" onClick={() => onResolve(incident.id)}>
                              Resolve
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
